import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Lock, User, Shield, AlertTriangle, RefreshCcw, KeyRound } from "lucide-react";
import { toast } from "sonner";

export default function SettingsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  return (
    <div className="p-8 max-w-3xl" data-testid="settings-page">
      <div className="eyb-label">Account</div>
      <h1 className="font-heading text-3xl font-bold text-slate-900 mt-1">Settings</h1>
      <p className="text-sm text-slate-500 mt-1 mb-8">Manage your account, security and preferences.</p>

      <div className="eyb-card p-6 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <User className="w-4 h-4 text-slate-400" />
          <h2 className="font-heading text-lg font-semibold text-slate-900">Profile</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-4">
          <Info label="Name" value={user?.name} />
          <Info label="Role" value={(user?.role || "").replace("_", " ")} />
          <Info label="Email" value={user?.email} wide />
        </div>
      </div>

      <ChangePasswordCard />
      {isAdmin && <DangerZoneCard />}
    </div>
  );
}

function DangerZoneCard() {
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (confirm !== "RESET") { toast.error('Type "RESET" exactly to confirm'); return; }
    if (!password) { toast.error("Password is required"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/admin/reset-data", { password, confirm });
      const totals = Object.entries(data.deleted).map(([k, v]) => `${k}: ${v}`).join(" · ");
      toast.success(`Reset complete — ${totals}`);
      setOpen(false); setPassword(""); setConfirm("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSubmitting(false); }
  };

  return (
    <div className="eyb-card border-rose-200 bg-rose-50/30 p-6 mt-6" data-testid="danger-zone-card">
      <div className="flex items-center gap-2 mb-2">
        <AlertTriangle className="w-4 h-4 text-rose-500" />
        <h2 className="font-heading text-lg font-semibold text-rose-700">Danger Zone — Reset all data</h2>
      </div>
      <p className="text-xs text-slate-600 mb-4">
        Wipes every <span className="font-semibold">lead, opportunity, activity, note, task, goal and audit log</span> in this workspace.
        Users, partnerships, partner firms, service categories and campaigns are preserved. <span className="font-semibold">This cannot be undone.</span>
      </p>
      {!open ? (
        <button
          data-testid="reset-data-open-btn"
          onClick={() => setOpen(true)}
          className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold border border-rose-300 text-rose-700 hover:bg-rose-100 rounded-md"
        >
          <RefreshCcw className="w-3.5 h-3.5" /> Reset transactional data
        </button>
      ) : (
        <form onSubmit={submit} className="space-y-3 max-w-md">
          <label className="block">
            <span className="eyb-label">Type <span className="font-mono font-bold text-slate-900">RESET</span> to confirm</span>
            <input
              data-testid="reset-data-confirm"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-md text-sm"
              placeholder="RESET"
              required
            />
          </label>
          <label className="block">
            <span className="eyb-label flex items-center gap-1.5"><KeyRound className="w-3.5 h-3.5" /> Your admin password</span>
            <input
              data-testid="reset-data-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-md text-sm"
              required
            />
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => { setOpen(false); setPassword(""); setConfirm(""); }}
              disabled={submitting}
              className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 rounded-md"
            >
              Cancel
            </button>
            <button
              type="submit"
              data-testid="reset-data-submit"
              disabled={submitting || confirm !== "RESET" || !password}
              className="px-4 py-2 bg-rose-600 hover:bg-rose-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-md"
            >
              {submitting ? "Resetting…" : "Reset all data"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

function Info({ label, value, wide }) {
  return (
    <div className={wide ? "sm:col-span-3 min-w-0" : "min-w-0"}>
      <div className="eyb-label">{label}</div>
      <div className="text-sm font-semibold text-slate-900 mt-1 capitalize break-all">{value || "—"}</div>
    </div>
  );
}

function ChangePasswordCard() {
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }
    if (form.new_password !== form.confirm) {
      toast.error("New password and confirmation do not match");
      return;
    }
    setSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      toast.success("Password changed successfully");
      setForm({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="eyb-card p-6">
      <div className="flex items-center gap-2 mb-1">
        <Lock className="w-4 h-4 text-slate-400" />
        <h2 className="font-heading text-lg font-semibold text-slate-900">Change Password</h2>
      </div>
      <p className="text-xs text-slate-500 mb-5 flex items-center gap-1.5">
        <Shield className="w-3 h-3" /> Minimum 6 characters. Use a mix of letters, numbers and symbols.
      </p>
      <form onSubmit={submit} className="space-y-4 max-w-md">
        <PwdField label="Current password" testid="current-password" value={form.current_password} onChange={(v) => setForm({ ...form, current_password: v })} />
        <PwdField label="New password" testid="new-password" value={form.new_password} onChange={(v) => setForm({ ...form, new_password: v })} />
        <PwdField label="Confirm new password" testid="confirm-password" value={form.confirm} onChange={(v) => setForm({ ...form, confirm: v })} />
        <div className="pt-2">
          <button
            data-testid="submit-change-password"
            type="submit"
            disabled={saving || !form.current_password || !form.new_password || !form.confirm}
            className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#29ABE2] hover:bg-[#1f8ebf] disabled:opacity-50 text-white text-sm font-semibold"
          >
            {saving ? "Updating…" : "Update password"}
          </button>
        </div>
      </form>
    </div>
  );
}

function PwdField({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <span className="eyb-label">{label}</span>
      <input
        data-testid={testid}
        type="password"
        required
        minLength={6}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#29ABE2]/40"
      />
    </label>
  );
}
